import React, { useEffect, useId, useState } from 'react';
import { parseWholeNumberInput } from './bundleWorkOrderLogic';

interface BundleWorkBaselineModalProps {
  readonly open: boolean;
  readonly baselineQty: number;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (baselineQty: number) => void;
}

function BundleWorkBaselineModal({
  open,
  baselineQty,
  submitting,
  error,
  onCancel,
  onConfirm,
}: BundleWorkBaselineModalProps): React.ReactElement | null {
  const inputId = useId();
  const [value, setValue] = useState(String(baselineQty));
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setValue(String(baselineQty));
    setLocalError(null);
  }, [open, baselineQty]);

  if (!open) {
    return null;
  }

  const displayError = localError ?? error;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    const parsed = parseWholeNumberInput(value);
    if (!parsed.ok) {
      setLocalError(parsed.error);
      return;
    }
    setLocalError(null);
    onConfirm(parsed.qty);
  };

  return (
    <div className="pbl-modal-overlay bundle-work-complete-modal-overlay" onClick={onCancel} role="presentation">
      <div
        className="pbl-modal bundle-work-complete-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby={`${inputId}-title`}
        aria-modal="true"
      >
        <button type="button" className="pbl-modal-close" onClick={onCancel} aria-label="Close">
          ×
        </button>
        <h3 id={`${inputId}-title`} className="bundle-work-complete-modal-title">
          Change Baseline
        </h3>
        <form className="bundle-work-complete-modal-form" onSubmit={handleSubmit}>
          <label className="bundle-work-complete-modal-label" htmlFor={inputId}>
            Baseline
          </label>
          <input
            id={inputId}
            className="bundle-work-complete-modal-input"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            disabled={submitting}
            onChange={(event) => setValue(event.target.value)}
            onFocus={(event) => event.target.select()}
          />
          {displayError ? <p className="bundle-work-complete-modal-error">{displayError}</p> : null}
          <div className="bundle-work-complete-modal-actions">
            <button
              type="button"
              className="bundle-work-complete-modal-btn bundle-work-complete-modal-btn-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bundle-work-complete-modal-btn bundle-work-complete-modal-btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BundleWorkBaselineModal;
